import React, { useEffect, useState, useCallback } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import DateRangePicker from "@amzn/meridian/date-range-picker"
import { DateRange } from '../../../services/equipment/types';
import { addDays, formatDate } from './date-range-utils';

interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onDateRangeChange: (dateRange: DateRange) => void;
  maxDate?: string;
  minDate?: string;
}

/**
 * DateRangeSelector component for filtering historical data
 */
export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
  maxDate = new Date().toISOString().split('T')[0],
  minDate,
}) => {
  // Initialize with props values
  const [dateRange, setDateRange] = useState([startDate || "", endDate || ""]);

  // Update internal state when props change
  useEffect(() => {
    setDateRange([startDate || "", endDate || ""]);
  }, [startDate, endDate]);

  const handleDateChange = useCallback((dates: string[]) => {
    if (dates.length === 2) {
      setDateRange(dates);
      onDateRangeChange({
        startDate: dates[0],
        endDate: dates[1],
      });
    }
  }, [onDateRangeChange]);

  const today = formatDate(new Date());
  const oneMonthAgo = formatDate(addDays(new Date(), -30));
  const threeMonthsAgo = formatDate(addDays(new Date(), -30 * 3));
  const sixMonthsAgo = formatDate(addDays(new Date(), -30 * 6));

  return (
    <Column spacing="200">
      <Row alignmentHorizontal="start">
        <DateRangePicker
          value={dateRange}
          onChange={handleDateChange}
          startLabel="Start date"
          endLabel="End date"
          legend="Select date range"
          locale="en-GB"
          monthsInView={2}
          presets={[
            { label: "Last 30 days", value: [oneMonthAgo, today] },
            { label: "Last 90 days", value: [threeMonthsAgo, today] },
            { label: "Last 180 days", value: [sixMonthsAgo, today] },
          ]}
          size="small"
        />
      </Row>
    </Column>
  );
};

export default DateRangeSelector;